import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Users, MessageSquare, Target, Calendar, Send, AlertCircle, CheckCircle, Share2, Clock, ArrowLeft, ArrowRight, Sparkles, Hash, Globe, Smile, Briefcase, Heart, Zap, BookOpen, MessageCircle, FileText, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { streamOpenAIResponse } from '../lib/openai';

interface CommunityForm {
  tone: string;
  category: string;
  platforms: string[];
  discordServerName?: string;
  redditSubreddit?: string;
  facebookGroupName?: string;
}

const CommunityBuilder = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [form, setForm] = useState<CommunityForm>({
    tone: '',
    category: '',
    platforms: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<{
    welcomeMessages: Record<string, string>;
    dailyTopics: string[];
    rules: string[];
    description: string;
  } | null>(null);

  const tones = [
    { id: 'witty', label: 'Witty & Fun', icon: Smile },
    { id: 'professional', label: 'Professional', icon: Briefcase },
    { id: 'friendly', label: 'Friendly & Casual', icon: Heart },
    { id: 'energetic', label: 'Energetic', icon: Zap },
    { id: 'educational', label: 'Educational', icon: BookOpen }
  ];

  const categories = [
    { id: 'tech', label: 'Technology & Innovation' },
    { id: 'business', label: 'Business & Entrepreneurship' },
    { id: 'creative', label: 'Creative & Design' },
    { id: 'lifestyle', label: 'Lifestyle & Wellness' },
    { id: 'education', label: 'Education & Learning' }
  ];

  const platforms = [
    { id: 'discord', label: 'Discord Server', icon: MessageCircle },
    { id: 'reddit', label: 'Reddit Community', icon: Globe },
    { id: 'facebook', label: 'Facebook Group', icon: Share2 }
  ];

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Save campaign data
      const { data: campaign, error: campaignError } = await supabase
        .from('community_campaigns')
        .insert({
          user_id: user.id,
          tone: form.tone,
          category: form.category,
          platforms: form.platforms,
          discord_server_name: form.discordServerName,
          reddit_subreddit: form.redditSubreddit,
          facebook_group_name: form.facebookGroupName
        })
        .select()
        .single();

      if (campaignError) throw campaignError;

      // Generate content using OpenAI
      const prompt = `Create a community building campaign with the following details:
Tone: ${form.tone}
Category: ${form.category}
Platforms: ${form.platforms.join(', ')}

Format your response as JSON with the following structure:
{
  "welcomeMessages": {
    "discord": "welcome message for Discord",
    "reddit": "welcome message for Reddit",
    "facebook": "welcome message for Facebook"
  },
  "dailyTopics": [
    "conversation starter 1",
    "conversation starter 2",
    "conversation starter 3"
  ],
  "rules": [
    "community rule 1",
    "community rule 2",
    "community rule 3"
  ],
  "description": "community description"
}`;

      let responseText = '';

      await streamOpenAIResponse(
        [{ role: 'user', content: prompt }],
        (chunk) => {
          responseText += chunk;
          try {
            const data = JSON.parse(responseText);
            setGeneratedContent(data);
          } catch (e) {
            // Partial response, continue collecting
          }
        },
        (error) => {
          throw error;
        }
      );

      // Save generated content
      if (generatedContent) {
        const contentPromises = form.platforms.map(platform => 
          supabase
            .from('community_campaign_content')
            .insert({
              campaign_id: campaign.id,
              content: generatedContent.welcomeMessages[platform],
              platform,
              content_type: 'welcome_message'
            })
        );

        // Save daily topics
        generatedContent.dailyTopics.forEach((topic, index) => {
          contentPromises.push(
            supabase
              .from('community_campaign_content')
              .insert({
                campaign_id: campaign.id,
                content: topic,
                platform: 'all',
                content_type: 'daily_topic',
                scheduled_for: new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toISOString()
              })
          );
        });

        await Promise.all(contentPromises);
      }

    } catch (err) {
      console.error('Error creating community campaign:', err);
      setError(err instanceof Error ? err.message : 'Failed to create community campaign');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Community Tone
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {tones.map(tone => {
                  const Icon = tone.icon;
                  return (
                    <motion.button
                      key={tone.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setForm(prev => ({ ...prev, tone: tone.id }))}
                      className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                        form.tone === tone.id
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                      }`}
                    >
                      <div className={`p-2 rounded-lg ${
                        form.tone === tone.id
                          ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                          : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                      }`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <span className="ml-3 font-medium text-gray-900 dark:text-white">
                        {tone.label}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Community Category
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {categories.map(category => (
                  <motion.button
                    key={category.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setForm(prev => ({ ...prev, category: category.id }))}
                    className={`flex items-center p-4 rounded-lg border-2 transition-all ${
                      form.category === category.id
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-dark-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                    }`}
                  >
                    <span className="font-medium text-gray-900 dark:text-white">
                      {category.label}
                    </span>
                    {form.category === category.id && (
                      <CheckCircle className="ml-auto h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    )}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Platforms
              </label>
              <div className="grid grid-cols-1 gap-4">
                {platforms.map(platform => {
                  const Icon = platform.icon;
                  const isSelected = form.platforms.includes(platform.id);
                  
                  return (
                    <motion.div
                      key={platform.id}
                      whileHover={{ scale: 1.01 }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-dark-700'
                      }`}
                    >
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${
                          isSelected
                            ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                            : 'bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-gray-400'
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className="ml-3 font-medium text-gray-900 dark:text-white">
                          {platform.label}
                        </span>
                        <button
                          onClick={() => setForm(prev => ({
                            ...prev,
                            platforms: isSelected
                              ? prev.platforms.filter(p => p !== platform.id)
                              : [...prev.platforms, platform.id]
                          }))}
                          className={`ml-auto px-3 py-1 rounded-lg text-sm transition-colors ${
                            isSelected
                              ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400'
                              : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {isSelected ? 'Selected' : 'Select'}
                        </button>
                      </div>
                      
                      {isSelected && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4"
                        >
                          {platform.id === 'discord' && (
                            <input
                              type="text"
                              value={form.discordServerName || ''}
                              onChange={(e) => setForm(prev => ({
                                ...prev,
                                discordServerName: e.target.value
                              }))}
                              placeholder="Enter desired server name"
                              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                            />
                          )}
                          {platform.id === 'reddit' && (
                            <input
                              type="text"
                              value={form.redditSubreddit || ''}
                              onChange={(e) => setForm(prev => ({
                                ...prev,
                                redditSubreddit: e.target.value
                              }))}
                              placeholder="Enter desired subreddit name"
                              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                            />
                          )}
                          {platform.id === 'facebook' && (
                            <input
                              type="text"
                              value={form.facebookGroupName || ''}
                              onChange={(e) => setForm(prev => ({
                                ...prev,
                                facebookGroupName: e.target.value
                              }))}
                              placeholder="Enter desired group name"
                              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
                            />
                          )}
                        </motion.div>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!generatedContent) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Your Community Setup
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Here's everything you need to launch your community
          </p>
        </div>

        {/* Welcome Messages */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <MessageSquare className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Welcome Messages
          </h3>
          {Object.entries(generatedContent.welcomeMessages)
            .filter(([platform]) => form.platforms.includes(platform))
            .map(([platform, message], index) => (
              <motion.div
                key={platform}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
              >
                <div className="flex items-center space-x-2 mb-3">
                  <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-full text-sm capitalize">
                    {platform}
                  </span>
                </div>
                <p className="text-gray-900 dark:text-white whitespace-pre-wrap">{message}</p>
                <div className="mt-4 flex justify-end">
                  <button className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm">
                    Copy Message
                  </button>
                </div>
              </motion.div>
            ))}
        </div>

        {/* Community Rules */}
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-xl p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100">
              Community Rules
            </h3>
          </div>
          <div className="space-y-3">
            {generatedContent.rules.map((rule, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 bg-white dark:bg-dark-800/50 rounded-lg p-4"
              >
                <span className="text-indigo-600 dark:text-indigo-400 font-medium">
                  {index + 1}.
                </span>
                <p className="text-gray-700 dark:text-gray-300">{rule}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Daily Topics */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <Calendar className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Daily Conversation Starters
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {generatedContent.dailyTopics.map((topic, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-dark-800 rounded-xl p-6 border border-gray-100 dark:border-dark-700"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Day {index + 1}
                  </span>
                  <button className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700">
                    <Settings className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-gray-900 dark:text-white">{topic}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Community Description */}
        <div className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center space-x-3 mb-4">
            <Globe className="h-5 w-5" />
            <h3 className="text-lg font-semibold">Community Description</h3>
          </div>
          <p className="text-purple-100 whitespace-pre-wrap">{generatedContent.description}</p>
          <button className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-sm transition-colors">
            Copy Description
          </button>
        </div>

        <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-dark-700">
          <button
            onClick={() => {
              setGeneratedContent(null);
              setCurrentStep(0);
              setForm({
                tone: '',
                category: '',
                platforms: [],
              });
            }}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            Start Over
          </button>
          <div className="space-x-4">
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate More
            </button>
            <button
              onClick={() => navigate('/calendar')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Schedule Posts
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Creating your community setup...</p>
        </div>
      ) : generatedContent ? (
        renderResults()
      ) : (
        <div className="space-y-8">
          {/* Progress bar */}
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-indigo-600 bg-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-400">
                  Progress
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-indigo-600 dark:text-indigo-400">
                  {Math.round((currentStep / 1) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-indigo-200 dark:bg-indigo-900/40">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${(currentStep / 1) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-indigo-600"
              />
            </div>
          </div>

          {renderStep()}

          <div className="flex justify-between pt-6">
            {currentStep > 0 ? (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </button>
            ) : (
              <button
                onClick={() => navigate('/campaign-generator')}
                className="flex items-center px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Cancel
              </button>
            )}
            <button
              onClick={() => {
                if (currentStep < 1) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  handleSubmit();
                }
              }}
              disabled={
                currentStep === 0 ? !form.tone || !form.category :
                form.platforms.length === 0
              }
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                (currentStep === 0 && form.tone && form.category) ||
                (currentStep === 1 && form.platforms.length > 0)
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === 1 ? (
                <>
                  Create Community
                  <Users className="h-4 w-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityBuilder;